import { ref } from 'vue'
import { api, endpoints } from '../services/api.js'

// State réactif partagé (singleton hors du setup)
const unreadCount = ref(0)
const notifications = ref([])
const isLoading = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const hasMore = ref(false)
let pollingInterval = null

export function useNotifications() {

  async function fetchUnreadCount() {
    try {
      const data = await api.get(endpoints.notifications.unreadCount())
      unreadCount.value = data.unreadCount || 0
    } catch (err) {
      console.error('Error fetching unread count:', err)
    }
  }

  async function fetchNotifications({ page = 1, limit = 20, unreadOnly = false } = {}) {
    isLoading.value = true
    try {
      const params = new URLSearchParams({ page, limit, unread_only: unreadOnly })
      const data = await api.get(`${endpoints.notifications.list()}?${params}`)
      notifications.value = data.notifications || []
      unreadCount.value = data.unreadCount || 0
      currentPage.value = data.page || 1
      totalPages.value = data.totalPages || 1
      hasMore.value = (data.page || 1) < (data.totalPages || 1)
      return data
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore() {
    if (!hasMore.value || isLoading.value) return
    isLoading.value = true
    try {
      const nextPage = currentPage.value + 1
      const params = new URLSearchParams({ page: nextPage, limit: 20 })
      const data = await api.get(`${endpoints.notifications.list()}?${params}`)
      notifications.value = [...notifications.value, ...(data.notifications || [])]
      unreadCount.value = data.unreadCount || 0
      currentPage.value = data.page || nextPage
      totalPages.value = data.totalPages || 1
      hasMore.value = (data.page || nextPage) < (data.totalPages || 1)
    } catch (err) {
      console.error('Error loading more notifications:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function markAsRead(id) {
    await api.patch(endpoints.notifications.markRead(id))
    const notif = notifications.value.find(n => n.id === id)
    if (notif) {
      notif.is_read = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
  }

  async function markAllAsRead() {
    await api.patch(endpoints.notifications.markAllRead())
    notifications.value.forEach(n => { n.is_read = true })
    unreadCount.value = 0
  }

  async function deleteNotification(id) {
    await api.delete(endpoints.notifications.delete(id))
    const notif = notifications.value.find(n => n.id === id)
    if (notif && !notif.is_read) {
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
    notifications.value = notifications.value.filter(n => n.id !== id)
  }

  function startPolling(intervalMs = 30000) {
    stopPolling()
    pollingInterval = setInterval(fetchUnreadCount, intervalMs)
  }

  function stopPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
  }

  return {
    unreadCount,
    notifications,
    isLoading,
    hasMore,
    fetchUnreadCount,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    startPolling,
    stopPolling
  }
}
